<template>
  <div class="method-preview">
    <MethodPreviewReact
      v-if="method && deviceConfiguration"
      :method="method"
      :methodOverride="methodOverride"
      :deviceConfiguration="deviceConfiguration"
      :isReadOnly="true"
    />
    <LoadingComponent v-else-if="isLoading" />
  </div>
</template>

<script>
  import LoadingComponent from 'components/element/LoadingComponent';
  import MethodPreviewReact from 'components/blocks/reactComponents/MethodPreview';
  import MethodAPI from '@/api/method';
  import { apiInjections } from '@/api/graphql/cloud/injections';

  export default {
    name: 'MethodPreview',

    components: {
      LoadingComponent,
      MethodPreviewReact,
    },

    props: {
      methodId: {
        type: Number,
        required: true,
      },
      injectionId: {
        type: Number,
        default: null,
      },
    },

    data: () => ({
      method: null,
      isLoading: false,
      methodOverride: null,
    }),

    computed: {
      deviceConfiguration() {
        return this.method?.hardware_configuration;
      },
    },

    watch: {
      methodId: {
        handler() {
          this.setMethod(null);
          this.initMethod();
        },
        immediate: true,
      },
      injectionId: {
        handler() {
          this.setMethod(null);
          this.initMethod();
        },
        immediate: true,
      },
    },

    methods: {
      setMethod(method) {
        if (!method) {
          this.method = null;
          return;
        }
        this.method = {
          ...method,
          is_editable: method.editable,
          is_favorite: method.favorite,
          is_archived: method.archived,
        };
      },

      setMethodOverride(methodOverride) {
        if (!methodOverride) {
          this.methodOverride = null;
          return;
        }
        this.methodOverride = {};

        if (methodOverride.injection_ul != null) {
          this.methodOverride.injectionUl = methodOverride.injection_ul;
        }
        if (methodOverride.run_time_ms != null) {
          this.methodOverride.runTimeMin = methodOverride.run_time_ms / 1000 / 60;
        }
      },

      async initMethod() {
        this.isLoading = true;

        try {
          if (this.injectionId) {
            const injection = await apiInjections.getInjectionById(this.injectionId);
            this.setMethod(injection.method);
            this.setMethodOverride(injection.method_override);
          } else {
            MethodAPI.get(
              this.methodId,
              (method) => {
                this.setMethod(method);
                this.isLoading = false;
              },
              () => {
                this.notifyError('Unable to get a method!');
                this.isLoading = false;
              },
            );
          }
          this.isLoading = false;
        } catch (error) {
          this.notifyError('Unable to get method data!');
          this.isLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .method-preview {
    h4 {
      margin-bottom: 0;
      padding-bottom: 0;
    }
    h5 {
      padding-top: 0;
      margin-top: 8px;
      margin-bottom: 32px;
    }

    @media (max-width: $screen-sm-max) {
      padding: 0 10px;
    }

    &__line {
      height: 2px;
      background: $color-bg-third;
      margin-bottom: 32px;
    }

    &__title {
      font-size: $size-xl;
      font-weight: $weight-semi-bold;
      margin-bottom: 30px;
    }

    &__data {
      margin-bottom: 20px;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
    }

    &__btn {
      margin-left: 15px;

      @media (max-width: $screen-sm-max) {
        &:first-child {
          margin-left: 0;
        }
      }
    }

    &__id {
      font-size: $size-xs;
      color: $color-text-third;
      margin-left: 5px;
    }
  }
</style>
